<template>
  <v-tooltip bottom :disabled="!mini">
    <template v-slot:activator="{ on }">
      <div
        ref="graphRoot"
        class="a-pricing-strategy-rule-graph"
        :style="{
          width: layout.width * scale + 'px',
          height: layout.height * scale + 'px'
        }"
        v-on="on"
      >
        <!-- Escenario a tamaño intrínseco, escalado para caber en el
             ancho disponible de la card -->
        <div
          class="graph-stage"
          :style="{
            width: layout.width + 'px',
            height: layout.height + 'px',
            transform: `scale(${scale})`
          }"
        >
          <!-- Conectores: misma geometría que el canvas (curva hasta la
               columna de bifurcación + tramo recto), color neutro -->
          <svg
            class="graph-connectors"
            :width="layout.width"
            :height="layout.height"
          >
            <path
              v-for="(path, i) in layout.paths"
              :key="i"
              :d="path.d"
              fill="none"
              :stroke="connector_stroke"
              :opacity="routeOpacity(path.route)"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>

          <!-- Nodos: chips redondeados (trigger contorno, condición tinte,
               acción sólida) -->
          <v-tooltip
            v-for="node in layout.nodes"
            :key="node.key"
            bottom
            :disabled="mini"
          >
            <template v-slot:activator="{ on: onNode }">
              <div
                v-on="onNode"
                class="graph-chip d-flex align-center justify-center"
                :style="chipStyle(node)"
              >
                <v-icon
                  :size="node.iconSize"
                  :color="node.kind === 'action' ? 'white' : color"
                >
                  {{ node.icon }}
                </v-icon>
              </div>
            </template>
            <div>
              <div>{{ node.tooltip }}</div>
              <!-- Segunda línea sólo en las condiciones de una regla que viene
                   de una sugerencia: qué se midió, o que ni se llegó a mirar -->
              <div v-if="node.measure" class="text-caption">
                {{ node.measure }}
              </div>
            </div>
          </v-tooltip>
        </div>
      </div>
    </template>

    <!-- Tooltip global (modo mini): detalle completo de la regla -->
    <div>
      <div class="font-weight-bold">{{ rule.name }}</div>
      <div>{{ triggerTooltip }}</div>
      <div v-for="(route, i) in routes" :key="`tooltip-route-${i}`">
        {{ routeTooltip(route) }}
        <template v-if="perRouteAction">
          → {{ routeActionTooltip(route) }}
        </template>
      </div>
      <div v-if="!perRouteAction">{{ actionTooltip }}</div>
    </div>
  </v-tooltip>
</template>

<script>
import {
  actionIcon,
  actionPhrase,
  hasPerRouteAction,
  routeActionPhrase,
  alphaColor,
  conditionPhrase,
  conditionMeasureValue,
  frequencyIcon,
  frequencyPhrase,
  routePhrase
} from "@/components/pricing-strategy/pricingStrategyUtils";
import { mapGetters } from "vuex";

// Mismo color que --rc-connector / RC_CONNECTOR_STROKE del canvas
const CONNECTOR_STROKE = "#fff";

export default {
  name: "a-pricing-strategy-rule-graph",
  props: {
    rule: {
      type: Object,
      required: true
    },
    color: {
      type: String,
      default: "#666666"
    },
    mini: {
      type: Boolean,
      default: false
    },
    // Justificación de la sugerencia que trajo esta regla, si viene de una.
    // Con ella el grafo ilumina la ruta que se siguió y apaga las demás.
    evaluation: {
      type: Object,
      default: null
    }
  },
  data: () => ({
    available_width: 0
  }),
  computed: {
    // Factor de escala para encajar el grafo en el ancho disponible
    // cuando la card es más estrecha que el grafo
    scale() {
      if (!this.available_width || !this.layout.width) return 1;
      return Math.min(1, this.available_width / this.layout.width);
    },
    connector_stroke() {
      return CONNECTOR_STROKE;
    },
    routes() {
      return this.rule.routes || [];
    },
    // Índice de la ruta que disparó, o null si no venimos de una sugerencia
    triggeredRouteIndex() {
      const index = this.evaluation ? this.evaluation.routeIndex : null;
      return Number.isInteger(index) ? index : null;
    },
    // Rutas evaluadas indexadas por su posición, para el tooltip de cada chip
    evaluatedRoutes() {
      const routes = (this.evaluation && this.evaluation.routes) || [];
      return routes.reduce((acc, route) => {
        acc[route.index] = route;
        return acc;
      }, {});
    },
    // Tres densidades para encajar en la altura fija de la fila del
    // grid (135px): normal, compacta (mini o 3 rutas) y densa (4+)
    density() {
      if (this.mini || this.routes.length >= 4) return "dense";
      if (this.routes.length === 3) return "compact";
      return "normal";
    },
    frequency() {
      return (this.rule.schedule && this.rule.schedule.frequency) || "";
    },
    triggerTooltip() {
      return (
        this.$t("pricing_strategy_graph_trigger") +
        ": " +
        frequencyPhrase(this, this.rule.schedule)
      );
    },
    actionTooltip() {
      return actionPhrase(this, this.rule.action);
    },
    // ¿La acción tiene un valor distinto por ruta? Entonces se pinta un chip
    // por ruta en vez de uno solo.
    perRouteAction() {
      return hasPerRouteAction(this.rule);
    },
    routeActionTooltip() {
      return route => routeActionPhrase(this, this.rule, route);
    },
    // Posiciones deterministas de chips y curvas: sin medir el DOM,
    // resistente a redimensionados de la card
    layout() {
      const SIZES = {
        normal: { C: 30, I: 15, LINK: 12, R: 10, L: 20 },
        compact: { C: 20, I: 12, LINK: 8, R: 6, L: 15 },
        dense: { C: 15, I: 10, LINK: 6, R: 4, L: 16 }
      };
      const { C, I, LINK, R, L } = SIZES[this.density];
      const PAD = 2;

      const routes = this.routes;
      const n = Math.max(routes.length, 1);
      const innerHeight = n * C + (n - 1) * R;
      const centerY = PAD + innerHeight / 2;

      const widths = routes.map(route => {
        const c = route.conditions.length;
        return c * C + (c - 1) * LINK;
      });
      const maxBranchWidth = Math.max.apply(null, widths.concat(C));

      const x0 = PAD;
      const branchX = x0 + C + L;
      const bifurcationX = x0 + C + L / 2;
      const actionX = branchX + maxBranchWidth + L;
      const mergeX = actionX - L / 2;

      const nodes = [];
      const paths = [];

      nodes.push({
        key: "trigger",
        kind: "trigger",
        icon: frequencyIcon(this.frequency),
        tooltip: this.triggerTooltip,
        x: x0,
        y: centerY - C / 2,
        size: C,
        iconSize: I
      });
      const actionIconName = actionIcon(
        (this.rule.action && this.rule.action.mode) || ""
      );
      if (!this.perRouteAction) {
        nodes.push({
          key: "action",
          kind: "action",
          icon: actionIconName,
          tooltip: this.actionTooltip,
          x: actionX,
          y: centerY - C / 2,
          size: C,
          iconSize: I
        });
      }

      const triggerRight = { x: x0 + C, y: centerY };
      const actionLeft = { x: actionX, y: centerY };

      routes.forEach((route, i) => {
        const midY = PAD + i * (C + R) + C / 2;
        let x = branchX;
        route.conditions.forEach((condition, j) => {
          if (j > 0) {
            // Tramo recto corto entre condiciones AND
            paths.push(`M ${x} ${midY} L ${x + LINK} ${midY}`);
            x += LINK;
          }
          nodes.push({
            key: `condition-${i}-${j}`,
            kind: "condition",
            route: i,
            icon: this.conditionIcon(condition),
            tooltip: this.conditionTooltip(condition),
            measure: this.conditionMeasureTooltip(i, j),
            x,
            y: midY - C / 2,
            size: C,
            iconSize: I
          });
          x += C;
        });
        paths.push({
          d: this.curve(triggerRight, { x: bifurcationX, y: midY }),
          route: i
        });
        paths.push({
          d: `M ${bifurcationX} ${midY} L ${branchX} ${midY}`,
          route: i
        });
        paths.push({ d: `M ${x} ${midY} L ${mergeX} ${midY}`, route: i });
        if (this.perRouteAction) {
          // Un chip de acción por ruta, a su misma altura. Mide lo mismo que
          // un chip de condición, así que layout.width/height no cambian y la
          // fila de 135px del grid no se ve afectada.
          nodes.push({
            key: `action-${i}`,
            kind: "action",
            route: i,
            icon: actionIconName,
            tooltip: this.routeActionTooltip(route),
            x: actionX,
            y: midY - C / 2,
            size: C,
            iconSize: I
          });
          paths.push({
            d: `M ${mergeX} ${midY} L ${actionX} ${midY}`,
            route: i
          });
        } else {
          paths.push({
            d: this.curve({ x: mergeX, y: midY }, actionLeft),
            route: i
          });
        }
      });

      return {
        width: actionX + C + PAD,
        height: innerHeight + PAD * 2,
        nodes,
        paths
      };
    },
    ...mapGetters({
      getPricingStrategyRuleTypeByCode: "getPricingStrategyRuleTypeByCode"
    })
  },
  mounted() {
    this.$nextTick(this.observeParentWidth);
  },
  updated() {
    // El activator del v-tooltip puede recrear el nodo tras el primer
    // render: re-observar si el padre actual ya no es el observado
    this.observeParentWidth();
  },
  beforeDestroy() {
    if (this._resize_observer) {
      this._resize_observer.disconnect();
    }
  },
  methods: {
    // Observar el ancho del contenedor padre (el wrapper centrado de la
    // card) para escalar el grafo cuando no quepa
    observeParentWidth() {
      const parent = this.$refs.graphRoot && this.$refs.graphRoot.parentElement;
      if (!parent || typeof ResizeObserver === "undefined") return;
      if (this._observed_parent === parent) return;
      if (!this._resize_observer) {
        this._resize_observer = new ResizeObserver(entries => {
          this.available_width = entries[0].contentRect.width;
        });
      } else {
        this._resize_observer.disconnect();
      }
      this._observed_parent = parent;
      this._resize_observer.observe(parent);
    },
    // Misma curva cúbica que los conectores del canvas (aPricingStrategyRuleCanvas)
    curve(a, b) {
      const dx = Math.max(8, Math.abs(b.x - a.x) * 0.55);
      return (
        `M ${a.x} ${a.y} ` +
        `C ${a.x + dx} ${a.y}, ${b.x - dx} ${b.y}, ${b.x} ${b.y}`
      );
    },
    // Opacidad de un trazo o chip según si pertenece a la ruta que disparó.
    // Sin justificación se pinta todo igual, como siempre.
    routeOpacity(routeIndex) {
      if (this.triggeredRouteIndex === null || routeIndex === undefined) {
        return 1;
      }
      return routeIndex === this.triggeredRouteIndex ? 1 : 0.25;
    },
    // Segunda línea del tooltip de una condición: el valor medido, o que la
    // ruta ni se llegó a evaluar porque otra anterior ya había disparado.
    conditionMeasureTooltip(routeIndex, conditionIndex) {
      const route = this.evaluatedRoutes[routeIndex];
      if (!route) return "";
      if (!route.evaluated) return this.$t("route_not_evaluated");

      const condition = (route.conditions || [])[conditionIndex];
      const value = conditionMeasureValue(this, condition);
      return value ? `${this.$t("measure")}: ${value}` : "";
    },
    chipStyle(node) {
      const style = {
        left: node.x + "px",
        top: node.y + "px",
        width: node.size + "px",
        height: node.size + "px",
        opacity: this.routeOpacity(node.route)
      };
      if (node.kind === "trigger") {
        style.background = "#fff";
        //style.border = `1.5px solid ${alphaColor(this.color, 0.4)}`;
      } else if (node.kind === "condition") {
        style.background = alphaColor("#fff", 0.5);
        //style.background = alphaColor(this.color, 0.2);
        //style.background = "#fff";
      } else {
        style.background = this.color;
        style.boxShadow = `0 1px 3px ${alphaColor(this.color, 0.45)}`;
      }
      return style;
    },
    conditionIcon(condition) {
      const rule_type = this.getPricingStrategyRuleTypeByCode(
        condition.rule_type_code
      );
      return (rule_type && rule_type.icon) || "mdi-tune";
    },
    // Frases centralizadas en pricingStrategyUtils.js
    conditionTooltip(condition) {
      return conditionPhrase(this, condition);
    },
    routeTooltip(route) {
      return routePhrase(this, route);
    }
  }
};
</script>

<style lang="scss" scoped>
.a-pricing-strategy-rule-graph {
  position: relative;
  flex: 0 0 auto;
  max-width: 100%;
  overflow: hidden;

  .graph-stage {
    position: absolute;
    top: 0;
    left: 0;
    transform-origin: top left;
  }

  .graph-connectors {
    position: absolute;
    top: 0;
    left: 0;
    overflow: visible;
    pointer-events: none;
  }

  .graph-chip {
    border-radius: 4px;
    position: absolute;
    z-index: 1;
    transition: transform 0.15s ease;
  }
}
</style>
